import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import {
  AttributeType,
  CondenserType,
  ConfigurationType,
  ZoneType
} from 'data/types';
import { isDuctedDesignConfiguration } from 'utils/ducted-design-predicates';
import { requiresDuctedSystemDesign } from 'utils/ducted-design-requirement';
import {
  DuctedPairIncompleteReason,
  DuctedPairLinkMode
} from 'utils/ducted-design-types';
import { getChooseSystemsEligibleDuctedSystems } from 'utils/existing-equipment';
import { HAS_EXISTING_EQUIPMENT_NO } from 'utils/existing-equipment-page-visibility';
import { isDuctedOnlySystemPreference } from 'utils/project';
import { isDuctedOnlyAirHandlerCode } from 'utils/resolve-zone-type';

function projectSettingValue(
  projectSettings: AttributeType[],
  code: string
): unknown {
  return projectSettings.find((setting) => setting.code === code)?.value;
}

function isDesigningNewDuctedSystems(
  projectSettings: AttributeType[]
): boolean {
  return (
    isDuctedOnlySystemPreference(
      projectSettingValue(projectSettings, 'system_preference')
    ) &&
    projectSettingValue(projectSettings, 'project_type') ===
      HAS_EXISTING_EQUIPMENT_NO
  );
}

export function getDuctedPairLinkMode(
  projectSettings: AttributeType[]
): DuctedPairLinkMode {
  return isDesigningNewDuctedSystems(projectSettings) ? 'automatic' : 'manual';
}

export function selectDuctedPairLinkMode(
  state: Pick<RootState, 'project'>
): DuctedPairLinkMode {
  return getDuctedPairLinkMode(state.project.projectSettings);
}

function zonesWithItems(condenser: CondenserType): ZoneType[] {
  return condenser.zones.filter((zone) => zone.zoneItems.length > 0);
}

export function getDuctedPairZone(condenser: CondenserType): ZoneType | null {
  const withItems = zonesWithItems(condenser);
  if (withItems.length === 1) return withItems[0];
  return condenser.zones.length === 1 ? condenser.zones[0] : null;
}

export function getDuctedPairIncompleteReason(
  condenser: CondenserType,
  eligibleSystemIds: ReadonlySet<string>
): DuctedPairIncompleteReason | null {
  if (condenser.position === undefined) return 'unpositioned';
  const zones = zonesWithItems(condenser);
  if (zones.length === 0) return 'no-zone';
  if (zones.length > 1) return 'multiple-zones';
  const [zone] = zones;
  if (!isDuctedOnlyAirHandlerCode(zone.airHandler?.airHandlerTypeCode)) {
    return 'no-air-handler';
  }
  if (!zone.existingSystemId || !eligibleSystemIds.has(zone.existingSystemId)) {
    return 'unlinked';
  }
  return null;
}

export function isDuctedPairComplete(
  condenser: CondenserType,
  eligibleSystemIds: ReadonlySet<string>
): boolean {
  return getDuctedPairIncompleteReason(condenser, eligibleSystemIds) === null;
}

export function getDuctedPairLinks(
  configuration: Pick<ConfigurationType, 'condensers'>
): string[] {
  return configuration.condensers
    .map((condenser) => getDuctedPairZone(condenser)?.existingSystemId ?? null)
    .filter((uniqueId): uniqueId is string => uniqueId !== null);
}

export function hasDistinctDuctedPairLinks(
  configuration: ConfigurationType
): boolean {
  const links = getDuctedPairLinks(configuration);
  return new Set(links).size === links.length;
}

export function getUnpairedDuctedSystemIds(
  configuration: Pick<ConfigurationType, 'condensers'>,
  eligibleSystemIds: ReadonlySet<string>
): string[] {
  const linked = new Set(getDuctedPairLinks(configuration));
  return Array.from(eligibleSystemIds).filter((id) => !linked.has(id));
}

export function isEveryDuctedSystemPaired(
  configuration: Pick<ConfigurationType, 'condensers'>,
  eligibleSystemIds: ReadonlySet<string>,
  linkMode: DuctedPairLinkMode
): boolean {
  return (
    linkMode === 'automatic' ||
    getUnpairedDuctedSystemIds(configuration, eligibleSystemIds).length === 0
  );
}

export function isDuctedDesignComplete(
  configuration: ConfigurationType | null | undefined,
  eligibleSystemIds: ReadonlySet<string>,
  linkMode: DuctedPairLinkMode
): boolean {
  if (!configuration || !isDuctedDesignConfiguration(configuration)) {
    return false;
  }
  if (configuration.condensers.length === 0) return false;
  return (
    configuration.condensers.every((condenser) =>
      isDuctedPairComplete(condenser, eligibleSystemIds)
    ) &&
    hasDistinctDuctedPairLinks(configuration) &&
    isEveryDuctedSystemPaired(configuration, eligibleSystemIds, linkMode)
  );
}

export function selectDuctedDesignRequired(
  state: Pick<RootState, 'project' | 'structure'>
): boolean {
  const systemPreference = state.project.projectSettings.find(
    (setting) => setting.code === 'system_preference'
  )?.value;
  if (!isDuctedOnlySystemPreference(systemPreference)) return false;
  const structure = Array.isArray(state.structure.structure)
    ? state.structure.structure
    : [];
  return requiresDuctedSystemDesign({
    structure,
    existingSystems: state.structure.existingSystems
  });
}

type StructureState = Pick<RootState, 'structure'>;

export const selectEligibleDuctedSystems = createSelector(
  [
    (state: StructureState) => state.structure.existingSystems,
    (state: StructureState) => state.structure.recommendationSelectionsBySystem,
    (state: StructureState) => state.structure.wantCompositionPicksBySystem
  ],
  (existingSystems, recommendationSelectionsBySystem, compositionPicks) =>
    getChooseSystemsEligibleDuctedSystems(
      existingSystems,
      recommendationSelectionsBySystem,
      compositionPicks
    )
);

export function selectEligibleDuctedSystemIds(
  state: StructureState
): Set<string> {
  return new Set(
    selectEligibleDuctedSystems(state).map((system) => system.uniqueId)
  );
}

export function selectDuctedPairExistingSystemLabel(
  state: Pick<RootState, 'project' | 'structure'>,
  systemUniqueId: string
): string | null {
  if (selectDuctedPairLinkMode(state) === 'automatic') return null;
  const eligibleSystems = selectEligibleDuctedSystems(state);
  if (eligibleSystems.length < 2) return null;
  return (
    eligibleSystems.find((system) => system.uniqueId === systemUniqueId)
      ?.name || 'Existing System'
  );
}

type DuctedDesignState = Pick<
  RootState,
  'project' | 'structure' | 'configurations'
>;

const NO_DUCTED_DESIGN_CONFIGURATION_IDS: ReadonlySet<string> = new Set();

const selectDesignConfigurationIdsOf = createSelector(
  [(configurations: ConfigurationType[]) => configurations],
  (configurations): ReadonlySet<string> =>
    new Set(
      configurations
        .filter(isDuctedDesignConfiguration)
        .map((configuration) => configuration.uniqueId)
    )
);

export function selectDuctedDesignConfigurationIds(
  state: DuctedDesignState
): ReadonlySet<string> {
  if (!selectDuctedDesignRequired(state)) {
    return NO_DUCTED_DESIGN_CONFIGURATION_IDS;
  }
  return selectDesignConfigurationIdsOf(state.configurations.configurations);
}

function isPopulatedDesign(
  designIds: ReadonlySet<string>,
  configuration: ConfigurationType
): boolean {
  return (
    designIds.has(configuration.uniqueId) && configuration.condensers.length > 0
  );
}

export function selectActiveDuctedDesign(
  state: DuctedDesignState
): ConfigurationType | null {
  const designIds = selectDuctedDesignConfigurationIds(state);
  if (designIds.size === 0) return null;
  const { configurations, activeConfig } = state.configurations;
  const active = configurations[activeConfig];
  if (active && isPopulatedDesign(designIds, active)) return active;
  return (
    configurations.find((configuration) =>
      isPopulatedDesign(designIds, configuration)
    ) ?? null
  );
}

export function selectActiveDuctedDesignIndex(
  state: DuctedDesignState
): number {
  const design = selectActiveDuctedDesign(state);
  return design ? state.configurations.configurations.indexOf(design) : -1;
}

export function selectIsActiveDuctedDesignComplete(state: RootState): boolean {
  const design = selectActiveDuctedDesign(state);
  if (!design) return false;
  return isDuctedDesignComplete(
    design,
    selectEligibleDuctedSystemIds(state),
    selectDuctedPairLinkMode(state)
  );
}

export function selectIsDuctedDesignPending(state: RootState): boolean {
  return (
    selectDuctedDesignRequired(state) &&
    !selectIsActiveDuctedDesignComplete(state)
  );
}
